import React, { useState } from 'react';
import './TeamLedCareSection.css';

export const teamMembers = [
  {
    id: 'fabio',
    tabLabel: 'Dr. Fabio Varlese',
    role: 'FOUNDER',
    name: 'Dr. Fabio Varlese',
    credentials: 'MD, MSc, FRCPC · Geriatric & internal medicine',
    summary: 'His work is dedicated to the quality of a life — and to innovation that reduces unnecessary intervention.',
    fullBio: 'Dr. Varlese founded The Toronto Clinic to offer physician-managed membership care for people who cannot piece a health strategy together from disconnected appointments. He is certified in internal medicine and geriatric medicine. The practice sits at 55 Avenue Road, in Yorkville, and has spent more than fifteen years building a collaborative, preventive model.',
    initials: 'FV',
    imageSrc: '/images/creatives/team-1.png',
  },
];

export const teamServices = [
  {
    id: 'care-nav',
    title: 'Care navigation',
    subtitle: 'Dedicated case management',
    desc: 'A person who knows the chart, chases results, and tells you what happens next.',
  },
  {
    id: 'naturopathic',
    title: 'Naturopathic care',
    subtitle: 'Collaborative clinicians',
    desc: 'Integrating evidence-based natural therapies into your comprehensive annual care strategy.',
  },
  {
    id: 'physiology',
    title: 'Physiology & nutrition',
    subtitle: 'In-house strategists',
    desc: 'Optimizing physical resilience, metabolic health, and performance through targeted lifestyle protocols.',
  },
  {
    id: 'advocacy',
    title: 'Advocacy',
    subtitle: 'When the system gets loud',
    desc: 'Direct clinical support and navigation through complex health scenarios and specialist networks.',
  }
];

function TeamLedCareSection() {
  const [activeTabId, setActiveTabId] = useState('fabio');
  const [activeServiceId, setActiveServiceId] = useState('care-nav');
  const [isBioExpanded, setIsBioExpanded] = useState(false);

  const activeMember = teamMembers.find(m => m.id === activeTabId) || teamMembers[0];

  const handleTabChange = (memberId) => {
    setActiveTabId(memberId);
    setIsBioExpanded(false);
  };

  return (
    <section className="ttc-team-section py-5 text-white">
      <div className="container py-md-4">
        
        {/* Header Row */}
        <div className="row align-items-start justify-content-between mb-5 g-4">
          <div className="col-12 col-md-6">
            <div className="ttc-team-tagline text-uppercase mb-2 d-flex align-items-center gap-2">
              <span className="ttc-team-bullet">•</span>
              <span>TEAM-LED CARE</span>
            </div>
            <h2 className="ttc-team-heading mb-0">
              Team-led <i>care.</i>
            </h2>
          </div>
          <div className="col-12 col-md-5 col-lg-4">
            <p className="ttc-team-subtext mb-0">
              For over 15 years, The Toronto Clinic has led with preventive, collaborative medicine — bringing the best minds together for people who expect more from it.
            </p>
          </div>
        </div>

        {/* Main Grid */}
        <div className="row g-4 align-items-stretch">
          
          {/* Left Column: Image Background Card with Bio */}
          <div className="col-12 col-lg-7 d-flex">
            <div 
              className="ttc-team-img-card p-4 p-md-5 rounded-4 w-100 d-flex flex-column justify-content-start position-relative overflow-hidden"
              style={{ backgroundImage: `url(${activeMember.imageSrc})` }}
            >
              {/* Dark Overlay for Readability */}
              <div className="ttc-card-bg-overlay position-absolute inset-0"></div>

              {/* Card Content Stacked Above Overlay */}
              <div className="position-relative z-2 w-100">
                {/* Doctor Switcher Pills */}
                <div className="d-flex flex-wrap gap-2 mb-4">
                  {teamMembers.map((member) => (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => handleTabChange(member.id)}
                      className={`ttc-team-pill-btn border-0 rounded-pill px-3 py-2 text-decoration-none ${
                        activeTabId === member.id ? 'active' : ''
                      }`}
                    >
                      {member.tabLabel}
                    </button>
                  ))}
                </div>

                {/* Bio Content */}
                <div className="ttc-bio-content-container mt-2">
                  <span className="ttc-member-role text-uppercase d-block mb-2">
                    {activeMember.role}
                  </span>
                  
                  <h3 className="ttc-member-name mb-2">
                    {activeMember.name}
                  </h3>
                  
                  <p className="ttc-member-credentials mb-4">
                    {activeMember.credentials}
                  </p>
                  
                  <p className="ttc-member-summary mb-3">
                    {activeMember.summary}
                  </p>

                  {/* Animated Collapsible Full Bio */}
                  <div className={`ttc-bio-expanded-wrapper ${isBioExpanded ? 'show' : ''}`}>
                    <div className="ttc-bio-expanded-content pt-2 pb-3">
                      <p className="ttc-member-fullbio mb-0">
                        {activeMember.fullBio}
                      </p>
                    </div>
                  </div>

                  {/* Toggle Button */}
                  <button
                    type="button"
                    onClick={() => setIsBioExpanded(!isBioExpanded)}
                    className="ttc-team-toggle-btn border-0 rounded-pill px-4 py-2 mt-2 fw-semibold"
                  >
                    {isBioExpanded ? 'Show less' : 'Learn more'}
                  </button>
                </div>
              </div>

            </div>
          </div>

          {/* Right Column: Solid Blue Card & Services Box */}
          <div className="col-12 col-lg-5 d-flex flex-column gap-3">
            
            {/* Right Top Card: Solid Blue Background */}
            <div className="ttc-blue-small-card p-4 rounded-4 text-white d-flex flex-column justify-content-end">
              <h4 className="ttc-doctor-initials mb-1">{activeMember.initials}</h4>
              <p className="ttc-doctor-card-credentials mb-0">
                {activeMember.credentials}
              </p>
            </div>

            {/* Bottom Interactive Services Box */}
            <div className="ttc-team-services-box p-3 p-md-4 rounded-4 bg-white text-dark flex-grow-1">
              <div className="d-flex flex-column gap-2">
                {teamServices.map((service) => {
                  const isActive = activeServiceId === service.id;
                  return (
                    <div
                      key={service.id}
                      onClick={() => setActiveServiceId(service.id)}
                      role="button"
                      tabIndex={0}
                      className={`ttc-service-item p-3 rounded-3 transition-all ${
                        isActive ? 'is-active bg-beige' : ''
                      }`}
                    >
                      <h5 className="ttc-service-title mb-1">{service.title}</h5>
                      <p className="ttc-service-subtitle text-muted mb-0">{service.subtitle}</p>
                      
                      {isActive && (
                        <p className="ttc-service-desc mt-3 mb-0 pt-2 border-top">
                          {service.desc}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
}

export default TeamLedCareSection;